import { PopoverGroup } from '@headlessui/react';
import ChevronIcon from '@otovo/shared/components/Icons/ChevronIcon/ChevronIcon';
import useLocalizedPath from '@otovo/shared/components/Navigation/useLocalizedPath';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import { useScrollTrigger } from '@otovo/shared/hooks/useIntersectionObserver';
import {
  Sanity$Document,
  Sanity$NavbarConfig,
} from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import DropdownItem from '../FlyOutMenu/DropdownItem';
import PopoverBackdrop from '../FlyOutMenu/PopoverBackdrop';
import DropdownContent from './DropdownContent';
import MenuButton from './MenuButton';
import MobileNavigation from './MobileNavigation';
import NavbarLogo from './NavbarLogo';

type NavbarProps = {
  navbarConfig: Sanity$NavbarConfig;
  sanityDocument: Sanity$Document;
};

const LanguageSwitcher = dynamic(
  () => import('@otovo/shared/components/Navigation/LanguageSwitcher'),
  {
    ssr: false,
  },
);

const Navbar = ({ navbarConfig, sanityDocument }: NavbarProps) => {
  const config = useGlobalConfig();
  const [solutionsOpen, setSolutionsOpen] = useState(false); // We store the state to propagate the background changes to the parent nav¨
  const [menuOpen, setMenuOpen] = useState(false); // TODO: Better way to solve this?

  // Fall back to a regular navbar on lack of browser support.
  const [fallbackStyles, setFallbackStyles] = useState(null);

  const [hasScrolledPast, isSupported] = useScrollTrigger(
    '#nav-scroll-trigger',
    {
      rootMargin: '-400px 0px 0px 0px',
    },
  );

  const isOpen = solutionsOpen || menuOpen;

  const colorScheme = hasScrolledPast
    ? 'color-scheme-white'
    : 'color-scheme-dark';

  const navStyle =
    isOpen || hasScrolledPast
      ? 'border-b border-b-heading/10 bg-surface/90 shadow-[0px_6px_16px_0px_rgba(13,18,28,0.06)] duration-300 backdrop-blur-md'
      : 'border-b border-b-transparent bg-transparent duration-200';

  useEffect(() => {
    if (!isSupported) {
      setFallbackStyles(
        'color-scheme-white border-b border-b-heading/10 bg-surface shadow-[0px_6px_16px_0px_rgba(13,18,28,0.06)] duration-300',
      );
    }
  }, [isSupported]);

  const localizedAboutUsPath = useLocalizedPath('/about');
  const localizedPricingPath = useLocalizedPath('/pricing');

  return (
    <>
      <PopoverBackdrop isVisible={isOpen} />
      <div
        className={cx(
          // Slight backdrop to ensure menu items are visible regardless of images.
          'fixed right-0 left-0 z-10 h-40 from-grey-100/60 transition-all duration-200',
          hasScrolledPast ? 'bg-transparent' : 'bg-linear-to-b',
        )}
      />
      <PopoverGroup
        as="nav"
        className={cx(
          'fixed top-0 z-60 w-full items-center transition-all',
          fallbackStyles ? fallbackStyles : [navStyle, colorScheme],
        )}
      >
        <div className="my-3 flex max-w-8xl items-center justify-between px-4 md:my-4 md:px-6 lg:px-16 2xl:mx-auto">
          {/* Logo / branding */}
          <NavbarLogo
            label={navbarConfig.ariaLabels.homeLinkLabel}
            variant={
              fallbackStyles || colorScheme === 'color-scheme-white'
                ? 'blue'
                : 'white'
            }
          />
          {/* Main navigation links, hidden on mobile */}
          <ul className="hidden sm:flex sm:gap-2 md:gap-4">
            <li key="solutions-desktop">
              <DropdownItem
                handleOpen={setSolutionsOpen}
                menuItem={
                  <>
                    {navbarConfig.topLevelLinkLabels.solutions}
                    <ChevronIcon className="h-4 w-4 rotate-90 group-data-[open]:-rotate-90" />
                  </>
                }
                className={colorScheme}
              >
                <DropdownContent
                  mainSection={navbarConfig.solutionsSection.packagePageSection}
                  subSection={navbarConfig.solutionsSection.productPageSection}
                  image={navbarConfig.solutionsSection.image}
                />
              </DropdownItem>
            </li>
            <li key="pricing-desktop">
              <Link
                href={`${localizedPricingPath}/`}
                className="flex items-center rounded-md px-3 py-2 text-base font-semibold text-heading"
              >
                {navbarConfig.topLevelLinkLabels.pricing}
              </Link>
            </li>
            <li key="about-desktop">
              <Link
                href={`${localizedAboutUsPath}/`}
                className="flex items-center rounded-md px-3 py-2 text-base font-semibold text-heading"
              >
                {navbarConfig.topLevelLinkLabels.aboutUs}
              </Link>
            </li>
          </ul>
          <div className="flex items-center">
            {config.BU_CONFIG.isMultiLocaleBu && (
              <LanguageSwitcher sanityDocument={sanityDocument} />
            )}

            <DropdownItem
              className={colorScheme}
              menuItem={
                <>
                  <span className="sr-only">
                    {navbarConfig.ariaLabels.menuButtonLabel}
                  </span>
                  <MenuButton isOpen={menuOpen} />
                </>
              }
              handleOpen={setMenuOpen}
            >
              <div className="hidden md:block">
                <DropdownContent
                  mainSection={navbarConfig.mainSection.sections[0]}
                  subSection={
                    navbarConfig.mainSection.sections?.[1] || undefined
                  }
                  image={navbarConfig.mainSection.image}
                />
              </div>
              <div className="block md:hidden">
                <MobileNavigation config={navbarConfig} />
              </div>
            </DropdownItem>
          </div>
        </div>
      </PopoverGroup>
    </>
  );
};
export default Navbar;
